import { DAYS_OF_WEEK, type DayOfWeek, type LocalTime, type RunnerSchedule } from '@/api/schedules';
import type { Subscription } from '@/stores/subscription';
import {
  DAY_MS,
  localWeekday,
  startOfLocalDay,
  toInstant,
  toLocal,
  zoneOffsets,
} from '@/lib/zone-time';

const MINUTES_PER_DAY = 24 * 60;
export const MAX_DURATION_MINUTES = MINUTES_PER_DAY;

/** Mirrors the backend's `SUBSCRIPTION_*` authorities on the schedule endpoints. */
const SCHEDULE_FLAVORS = new Set(['PRO', 'F_AND_F', 'ENTERPRISE']);

export function schedulesIncluded(subscription: Subscription | null): boolean {
  return subscription?.status === 'ACTIVE' && SCHEDULE_FLAVORS.has(subscription.flavor);
}

export const WEEKDAYS: DayOfWeek[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
export const WEEKEND: DayOfWeek[] = ['Sat', 'Sun'];
export const EVERY_DAY: DayOfWeek[] = [...DAYS_OF_WEEK];

export function sortDays(days: readonly DayOfWeek[]): DayOfWeek[] {
  return DAYS_OF_WEEK.filter((d) => days.includes(d));
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

export function minutesOf(time: LocalTime): number {
  return time.hour * 60 + time.minute;
}

export function formatTime(time: LocalTime): string {
  return `${pad2(time.hour)}:${pad2(time.minute)}`;
}

function formatMinuteOfDay(minutes: number): string {
  return formatTime({ hour: Math.floor(minutes / 60), minute: minutes % 60 });
}

/** Parses `HH:MM` as produced by `<input type="time">`; null for anything else. */
export function parseTime(value: string): LocalTime | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour < 24 && minute < 60 ? { hour, minute } : null;
}

/** "Every day", "Mon–Fri", "Mon, Wed, Fri" or "Mon–Wed, Sat, Sun". */
export function formatDays(days: readonly DayOfWeek[]): string {
  const indexes = sortDays(days).map((d) => DAYS_OF_WEEK.indexOf(d));
  if (indexes.length === 7) return 'Every day';
  const parts: string[] = [];
  for (let i = 0; i < indexes.length;) {
    let j = i;
    while (j + 1 < indexes.length && indexes[j + 1] === indexes[j]! + 1) j++;
    const first = DAYS_OF_WEEK[indexes[i]!];
    const last = DAYS_OF_WEEK[indexes[j]!];
    if (j - i >= 2) parts.push(`${first}–${last}`);
    else for (let k = i; k <= j; k++) parts.push(DAYS_OF_WEEK[indexes[k]!]!);
    i = j + 1;
  }
  return parts.join(', ');
}

/** "45 min", "8 h" or "8 h 30 min". */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

export function windowLength(schedule: RunnerSchedule): number {
  if (schedule.durationMinutes != null) return schedule.durationMinutes;
  return schedule.stopTime ? minutesOf(schedule.stopTime) - minutesOf(schedule.startTime) : 0;
}

/** "08:00–18:00" for a stop time, "22:00 for 4 h" for a duration. */
export function formatWindow(schedule: RunnerSchedule): string {
  const start = formatTime(schedule.startTime);
  if (schedule.stopTime) return `${start}–${formatTime(schedule.stopTime)}`;
  return `${start} for ${formatDuration(windowLength(schedule))}`;
}

export function scheduleSummary(schedule: RunnerSchedule): string {
  return [formatDays(schedule.daysOfWeek), formatWindow(schedule), schedule.zoneId].join(' · ');
}

export interface ScheduleEvent {
  kind: 'resume' | 'pause';
  day: DayOfWeek;
  time: string;
  /** On the current date in the schedule's zone. */
  today: boolean;
}

/**
 * The next time the planner resumes or pauses the runner, following `ScheduledRunnerPlanner`: the latest start at or
 * before an instant decides whether it is inside a window, so a start at or before the previous window's stop
 * continues it. A duration counts elapsed time across daylight saving changes; a stop time is on the start's date.
 * Null when the zone is unknown to this browser or nothing changes, as with windows covering the whole week.
 */
export function nextScheduleEvent(schedule: RunnerSchedule, now: Date): ScheduleEvent | null {
  if (!schedule.enabled || schedule.daysOfWeek.length === 0) return null;
  const offsetAt = zoneOffsets(schedule.zoneId);
  if (!offsetAt) return null;
  const nowMs = now.getTime();
  const today = startOfLocalDay(toLocal(nowMs, offsetAt));

  const windows: { start: number; stop: number }[] = [];
  // Covers the planner's seven-day look-back and a full week ahead.
  for (let offset = -8; offset <= 9; offset++) {
    const date = today + offset * DAY_MS;
    if (!schedule.daysOfWeek.includes(DAYS_OF_WEEK[localWeekday(date)]!)) continue;
    const start = toInstant(date + minutesOf(schedule.startTime) * 60_000, offsetAt);
    let stop = start;
    if (schedule.durationMinutes != null) {
      stop = start + schedule.durationMinutes * 60_000;
    } else if (schedule.stopTime) {
      const startDate = startOfLocalDay(toLocal(start, offsetAt));
      stop = toInstant(startDate + minutesOf(schedule.stopTime) * 60_000, offsetAt);
    }
    windows.push({ start, stop });
  }

  if (windows.length === 0) return null;
  const inWindow = (instant: number) => {
    const latest = windows.filter((w) => w.start <= instant).at(-1);
    return latest !== undefined && instant < latest.stop;
  };
  const running = inWindow(nowMs);
  const at = windows
    .flatMap((w) => [w.start, w.stop])
    // Beyond the last start, the computed windows end where the real schedule continues.
    .filter((instant) => instant > nowMs && instant <= windows.at(-1)!.start)
    .sort((a, b) => a - b)
    .find((instant) => inWindow(instant) !== running);
  if (at === undefined) return null;

  const local = toLocal(at, offsetAt);
  const minuteOfDay = Math.round((local - startOfLocalDay(local)) / 60_000);
  return {
    kind: running ? 'pause' : 'resume',
    day: DAYS_OF_WEEK[localWeekday(local)]!,
    time: formatMinuteOfDay(minuteOfDay),
    today: startOfLocalDay(local) === today,
  };
}

/** "pauses at 18:00" or "resumes Mon at 08:00". */
export function formatScheduleEvent(event: ScheduleEvent): string {
  const verb = event.kind === 'pause' ? 'pauses' : 'resumes';
  return event.today ? `${verb} at ${event.time}` : `${verb} ${event.day} at ${event.time}`;
}

export interface DaySegment {
  /** Index into DAYS_OF_WEEK. */
  day: number;
  /** Minutes since midnight; `to` is at most 1440. */
  from: number;
  to: number;
}

/** The active parts of each day; a window running past midnight continues on the next day's row. */
export function weeklySegments(
  days: readonly DayOfWeek[],
  startMinute: number,
  length: number
): DaySegment[] {
  const segments: DaySegment[] = [];
  for (const d of sortDays(days)) {
    const day = DAYS_OF_WEEK.indexOf(d);
    const end = startMinute + length;
    segments.push({ day, from: startMinute, to: Math.min(end, MINUTES_PER_DAY) });
    if (end > MINUTES_PER_DAY) {
      segments.push({ day: (day + 1) % 7, from: 0, to: end - MINUTES_PER_DAY });
    }
  }
  return segments;
}

export type StopMode = 'time' | 'duration';

/** The editor's state; times are `HH:MM` strings as bound to `<input type="time">`. */
export interface ScheduleForm {
  zoneId: string;
  start: string;
  stopMode: StopMode;
  stop: string;
  durationHours: number | null;
  durationMinutes: number | null;
  days: DayOfWeek[];
  enabled: boolean;
}

export type ScheduleField = 'zoneId' | 'start' | 'stop' | 'duration' | 'days';
export type ScheduleErrors = Partial<Record<ScheduleField, string>>;

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function isValidTimeZone(zoneId: string): boolean {
  if (!zoneId) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zoneId });
    return true;
  } catch {
    return false;
  }
}

export function scheduleForm(schedule: RunnerSchedule | null, defaultZone: string): ScheduleForm {
  if (!schedule) {
    return {
      zoneId: defaultZone,
      start: '08:00',
      stopMode: 'time',
      stop: '18:00',
      durationHours: 10,
      durationMinutes: 0,
      days: [...WEEKDAYS],
      enabled: true,
    };
  }
  const length = windowLength(schedule);
  const stopMinute = minutesOf(schedule.startTime) + length;
  return {
    zoneId: schedule.zoneId,
    start: formatTime(schedule.startTime),
    stopMode: schedule.stopTime ? 'time' : 'duration',
    // Switching modes keeps the same window where it fits in the day.
    stop: schedule.stopTime
      ? formatTime(schedule.stopTime)
      : stopMinute < MINUTES_PER_DAY
        ? formatMinuteOfDay(stopMinute)
        : '',
    durationHours: Math.floor(length / 60),
    durationMinutes: length % 60,
    days: sortDays(schedule.daysOfWeek),
    enabled: schedule.enabled,
  };
}

export function formDuration(form: ScheduleForm): number | null {
  const hours = form.durationHours ?? 0;
  const minutes = form.durationMinutes ?? 0;
  if (form.durationHours == null && form.durationMinutes == null) return null;
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || minutes < 0) {
    return null;
  }
  return hours * 60 + minutes;
}

/**
 * The backend's rules for a schedule, with messages for the editor. `storedZoneId` passes as is: the backend accepts
 * zones, such as `UTC+02:00`, that browsers cannot format.
 */
export function validateScheduleForm(form: ScheduleForm, storedZoneId?: string): ScheduleErrors {
  const errors: ScheduleErrors = {};
  if (form.zoneId !== storedZoneId && !isValidTimeZone(form.zoneId)) {
    errors.zoneId = 'Choose a time zone from the list.';
  }
  const start = parseTime(form.start);
  if (!start) errors.start = 'Enter a start time.';
  if (form.stopMode === 'time') {
    const stop = parseTime(form.stop);
    if (!stop) {
      errors.stop = 'Enter a stop time.';
    } else if (start && minutesOf(stop) <= minutesOf(start)) {
      errors.stop =
        'The stop time must be later than the start time on the same day. For a window past midnight, use a duration.';
    }
  } else {
    const duration = formDuration(form);
    if (duration == null || duration < 1 || duration > MAX_DURATION_MINUTES) {
      errors.duration = 'Enter a duration from 1 minute to 24 hours.';
    }
  }
  if (form.days.length === 0) errors.days = 'Choose at least one day.';
  return errors;
}

export function scheduleFromForm(form: ScheduleForm): RunnerSchedule {
  const byDuration = form.stopMode === 'duration';
  return {
    zoneId: form.zoneId,
    startTime: parseTime(form.start)!,
    stopTime: byDuration ? null : parseTime(form.stop),
    durationMinutes: byDuration ? formDuration(form) : null,
    daysOfWeek: sortDays(form.days),
    enabled: form.enabled,
  };
}
